import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { buyerSubmissionsApi, type BuyerSubmissionDetailDto } from "@/api/buyers";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import { Button, Textarea, useToast } from "@/design-system/components/ui";
import { buyerKeys, buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { DetailField, DetailFieldList, DetailSection } from "../detailBits";

/**
 * The submission's free-text notes (`BuyerSubmission.Notes`) — read, and edited.
 *
 * <p>🔴 <b>Kept when the Indication card went.</b> Notes used to live on that
 * card because its EDIT INDICATION form was their only writer. The field is
 * still live — escalation carries it into the deal as "Consultant notes on the
 * submission" — so retiring the Indication card must not take its only editor
 * with it.</p>
 *
 * <p>A blank save sends `""`, which the server's string patch trims to null: a
 * real clear.</p>
 */
export function SubmissionNotesCard({
  s,
  canWrite,
}: {
  s: BuyerSubmissionDetailDto;
  canWrite: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");

  const update = useMutation({
    mutationFn: (notes: string) => buyerSubmissionsApi.update(s.id, { notes }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      toast.success("Notes saved.");
      setOpen(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const canEdit = canWrite && s.canEdit;

  return (
    <DetailSection
      title="Notes"
      action={
        canEdit ? (
          <Button
            size="sm"
            variant="secondary"
            data-testid="submission-notes-edit"
            onClick={() => {
              setText(s.notes ?? "");
              setOpen(true);
            }}
          >
            Edit notes
          </Button>
        ) : undefined
      }
    >
      <DetailFieldList>
        <DetailField label="Consultant notes" value={s.notes?.trim() ? s.notes : null} wide />
      </DetailFieldList>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Edit notes"
        footer={(
          <div className="flex justify-end w-full gap-space-2">
            <Button variant="secondary" disabled={update.isPending} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="submission-notes-save"
              disabled={update.isPending}
              onClick={() => update.mutate(text.trim())}
            >
              Save
            </Button>
          </div>
        )}
      >
        <label htmlFor="submission-notes" className="block text-caption text-gray-mid mb-space-1">
          Consultant notes
        </label>
        <Textarea
          id="submission-notes"
          data-testid="submission-notes-input"
          rows={5}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </Modal>
    </DetailSection>
  );
}
